/** <product-card>: grid product card — hover/focus slides an inner <slide-show> to the second image, pagination and drag stay available for touch, and a click on a size quick-adds the variant through /cart/add.js then emits a CartAddEvent. */
import { CartAddEvent } from "@theme/events";
import { cartSectionIds } from "@theme/utilities";

class ProductCard extends HTMLElement {
  #mobileMq = null;
  #adding = false;

  /** Mobile view: moving to the 2nd image is disabled there; `(hover: none)` alone would not do since a desktop narrowed to mobile width keeps `hover: hover`, and this is evaluated on each event so a resize is picked up. */
  #mobileViewMq = window.matchMedia("(hover: none), (max-width: 750px)");

  connectedCallback() {
    // Ajout rapide au panier depuis les tailles de l'encart (délégué).
    this.addEventListener("click", this.#handleSizeClick);

    // Color switch is independent of the slider, so it is wired BEFORE the early return below (a card without a slider can still have colors).
    this.initColorsSwitch();

    this.slideShow = this.querySelector("slide-show");
    if (!this.slideShow) return;

    // Hover only makes sense on a pointer device while keyboard focus is wired in every case; both go through #mobileViewMq, since a tap emits `focusin` and used to make the slider jump.
    if (window.matchMedia("(hover: hover)").matches) {
      this.addEventListener("mouseenter", this.#showSecond);
      this.addEventListener("mouseleave", this.#showFirst);
    }
    this.addEventListener("focusin", this.#showSecond);
    this.addEventListener("focusout", this.#showFirst);

    // Minimalist card: the inner swiper drag is disabled under 750px so it cannot capture the horizontal gesture of a parent slider; the arrows still change image.
    if (this.classList.contains("minimalist")) {
      this.#mobileMq = window.matchMedia("(max-width: 750px)");
      this.slideShow.addEventListener("slide-show:ready", this.#syncMobileDrag);
      this.#mobileMq.addEventListener("change", this.#syncMobileDrag);
      this.#syncMobileDrag();
    }
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.#handleSizeClick);
    this.removeEventListener("mouseenter", this.#showSecond);
    this.removeEventListener("mouseleave", this.#showFirst);
    this.removeEventListener("focusin", this.#showSecond);
    this.removeEventListener("focusout", this.#showFirst);
    this.slideShow?.removeEventListener("slide-show:ready", this.#syncMobileDrag);
    this.#mobileMq?.removeEventListener("change", this.#syncMobileDrag);
  }

  /**
   * Click on a size: the link to the variant PDP is intercepted to add to cart directly; without JS the link keeps its native behaviour.
   * @param {MouseEvent} event
   */
  #handleSizeClick = (event) => {
    // Backdrop de l'encart : un tap referme. Testé en premier — il couvre la
    // carte, donc tout clic sur celle-ci hors de l'encart lui parvient.
    const backdrop = event.target.closest("[data-variants-backdrop]");
    if (backdrop && this.contains(backdrop)) {
      event.preventDefault();
      this.#setVariantsOpen(false);
      return;
    }

    // Bouton « + » mobile : ouvre/ferme l'encart tailles·couleurs. Testé avant
    // le quick-add, il est hors de l'encart donc les deux ne se recoupent pas.
    const toggle = event.target.closest("[data-variants-toggle]");
    if (toggle && this.contains(toggle)) {
      event.preventDefault();
      this.#setVariantsOpen(this.dataset.variantsOpen !== "true");
      return;
    }

    // Cible aussi bien une taille (.product_card__size) que le bouton d'ajout
    // d'un produit mono-variante (.product_card__add) : tous deux [data-quick-add].
    const trigger = event.target.closest("[data-quick-add]");
    if (!trigger || !this.contains(trigger)) return;

    // Modificateurs (nouvel onglet, etc.) : on laisse le lien natif agir.
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();

    // Variante indisponible : pas d'ajout possible.
    if (trigger.classList.contains("is-unavailable")) return;

    const variantId = trigger.dataset.variantId;
    if (variantId) this.#addToCart(variantId, trigger);
  };

  /**
   * Opens/closes the sizes/colors panel (mobile); the state lives on the card and the CSS displays it, with nothing to sync on `aria-hidden` since `visibility: hidden` already carries the closed state.
   * @param {boolean} open
   */
  #setVariantsOpen(open) {
    const panel = this.querySelector(".product_card__variants");
    if (!panel) return;

    this.dataset.variantsOpen = open ? "true" : "false";

    const trigger = this.querySelector("[data-variants-toggle]");
    if (trigger) {
      trigger.setAttribute("aria-expanded", open ? "true" : "false");
      if (!panel.id) {
        panel.id = `card-variants-${Math.random().toString(36).slice(2, 9)}`;
      }
      trigger.setAttribute("aria-controls", panel.id);
    }
  }

  /** Wires the color switch on this card's swatches only (`this.querySelectorAll`), so there is no cross-rebinding and the listeners are freed with the card. */
  initColorsSwitch() {
    const colors = this.querySelectorAll(".product_card__colors_list .product_card__color");
    colors.forEach((link) => link.addEventListener("click", this.#handleColorClick));
  }

  /**
   * Click on a color swatch: fetches the linked product's card through the Section Rendering API and swaps it in; without JS the link keeps its native behaviour.
   * @param {MouseEvent} event
   */
  #handleColorClick = async (event) => {
    const link = event.currentTarget;

    // Modificateurs (nouvel onglet, etc.) : on laisse le lien natif agir.
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();

    // Couleur déjà affichée : rien à faire.
    if (link.classList.contains("is-active")) return;

    const targetHref = link.getAttribute("href");
    const targetHandle = this.#productHandle(targetHref);

    // Handle illisible → on ne peut pas garantir la bonne carte : lien natif.
    if (!targetHandle) {
      window.location.assign(targetHref);
      return;
    }

    // The URL is rebuilt on the current localized root rather than taken from the raw href: a wrong market prefix makes Shopify redirect and LOSE `?section_id`, returning the whole product page instead of the card.
    const root = window.Shopify?.routes?.root || "/";
    const sectionUrl = `${root}products/${targetHandle}?section_id=card-product-ajax`;

    // Timeout de 2 s (équivalent du `timeout` jQuery) via AbortController.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 2000);

    try {
      const response = await fetch(sectionUrl, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const cards = new DOMParser().parseFromString(html, "text/html").querySelectorAll(".product_card");

      // Guard: the card whose handle matches the clicked link is selected, never the first one found, which on a full page would be a recommendation.
      const newCard = [...cards].find((card) => this.#productHandle(card.getAttribute("data-product-url")) === targetHandle);

      if (!newCard) {
        console.warn("[product-card] switch couleur : carte introuvable → navigation native.");
        window.location.assign(targetHref);
        return;
      }

      this.#swapContent(newCard);
    } catch (error) {
      // Réseau/timeout : on ne bloque pas l'utilisateur, on suit le lien.
      console.error("[product-card] échec du switch couleur:", error);
      window.location.assign(targetHref);
    } finally {
      clearTimeout(timer);
    }
  };

  /**
   * Replaces this card's CONTENT while keeping the same <product-card> node, since Depict reorders the grid and would push an unknown node to the end; the connectedCallback does not fire again, so the inner slider and color switch are resynced by hand.
   * @param {Element} newCard
   */
  #swapContent(newCard) {
    this.replaceChildren(...newCard.childNodes);
    // The root node is kept, so the product-level `sold_out` class must be synced by hand or the sold-out veil would keep the previous color's state. Other root classes (minimalist) depend on the grid, not the product, and are left untouched.
    this.classList.toggle("sold_out", newCard.classList.contains("sold_out"));
    this.dataset.productId = newCard.getAttribute("data-product-id") ?? "";
    this.dataset.productUrl = newCard.getAttribute("data-product-url") ?? "";
    // The new markup arrives closed (toggle at aria-expanded="false"), so the card state must restart from zero or the CSS would leave the panel open.
    this.dataset.variantsOpen = "false";
    // Re-cible le slider interne (nouveau <slide-show>) pour le survol → 2e image.
    this.slideShow = this.querySelector("slide-show");
    // Re-branche le switch couleur sur les nouvelles pastilles.
    this.initColorsSwitch();
  }

  /**
   * Extracts the handle from a Shopify product URL, robust to language prefixes, query and trailing slash; used to check the AJAX card is the clicked product's.
   * @param {string|null} path
   * @returns {string}
   */
  #productHandle(path) {
    const match = (path || "").match(/\/products\/([^/?#]+)/);
    return match ? match[1] : "";
  }

  /**
   * Adds a variant to the cart then notifies the theme (CartAddEvent): the cart drawer opens and refreshes, the cart icon updates.
   * @param {string} variantId
   * @param {HTMLElement} source - l'élément taille cliqué (état visuel).
   */
  async #addToCart(variantId, source) {
    if (this.#adding) return;
    this.#adding = true;
    source.setAttribute("aria-busy", "true");

    try {
      const sections = cartSectionIds();
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          ...(sections.length && { sections: sections.join(","), sections_url: window.location.pathname }),
        }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const cart = await response.json();
      document.dispatchEvent(new CartAddEvent(cart, this.dataset.productId, { variantId, sections: cart.sections }));
    } catch (error) {
      console.error("[product-card] add to cart failed:", error);
    } finally {
      this.#adding = false;
      source.removeAttribute("aria-busy");
    }
  }

  /** Enables/disables the swiper touch drag per breakpoint: off on mobile so a parent slider can scroll, on for desktop. */
  #syncMobileDrag = () => {
    const swiper = this.#swiper();
    if (!swiper || !this.#mobileMq) return;
    const allow = !this.#mobileMq.matches;
    swiper.allowTouchMove = allow;
    if (swiper.params) swiper.params.allowTouchMove = allow;
  };

  /** Instance Swiper exposée par <slide-show> (null tant que pas init). */
  #swiper() {
    return this.slideShow?.swiper || null;
  }

  /** Goes to logical slide `index`: in loop mode Swiper inserts duplicated slides, so `slideToLoop` is required rather than `slideTo`. */
  #goTo(index) {
    const swiper = this.#swiper();
    if (!swiper) return;
    if (swiper.params?.loop && typeof swiper.slideToLoop === "function") {
      swiper.slideToLoop(index);
    } else {
      swiper.slideTo(index);
    }
  }

  /** Packshot view active for this card: an ancestor carries `.is-packshot-view` and the card really has an overlaid packshot, the class being set at runtime by the collection toggle. */
  #inPackshotView() {
    return Boolean(this.closest(".is-packshot-view") && this.querySelector(".product_card__packshot"));
  }

  // Hover advances to the 2nd slide and back on exit; a no-op in packshot view, where hover must reveal the 1st image under the packshot and the fade is handled in CSS.
  #showSecond = () => {
    if (this.#mobileViewMq.matches) return;
    if (this.#inPackshotView()) return;
    this.#goTo(1);
  };

  #showFirst = () => {
    if (this.#mobileViewMq.matches) return;
    if (this.#inPackshotView()) return;
    this.#goTo(0);
  };
}

if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCard);
}
